import React from 'react';

import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import { Box, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import CallBand from '../components/stitch/CallBand';
import NumberedCards from '../components/stitch/NumberedCards';
import PageHero from '../components/stitch/PageHero';
import Section, { cardSx } from '../components/stitch/Section';
import { tokens } from '../theme';

// Built to the Stitch "About Us" screen (project 1587770953174176333), with the site's own copy.
const stages = [
  ['Map the workflow', 'We map the workflow before choosing the tools.'],
  [
    'Explain the tradeoffs',
    'A useful solution may need a clear interface, an integration with an existing system, a better way to manage content, or all three. We explain the tradeoffs.',
  ],
  [
    'Build in reviewable stages',
    'We build in stages that can be reviewed as the work progresses, toward software that people can use, understand, and maintain after launch.',
  ],
] as const;

// Each check runs in Site CI (.gitea/workflows/site-ci.yml) before a change can merge.
const releaseChecks = [
  ['Code checks', 'Linting and formatting rules run on every source file.'],
  [
    'Automated tests',
    'More than 200 unit tests cover pages and components, alongside tests for the contact service, deployment scripts, and configuration.',
  ],
  [
    'Build and render',
    'The production site is built, and every public page is checked for correctly rendered content and metadata.',
  ],
  [
    'Browser checks',
    'Every public page is scanned for accessibility issues, including enhanced (AAA) contrast, keyboard focus, narrow screens, and enlarged text.',
  ],
  [
    'Privacy checks',
    'Tests confirm error reports leave out form entries and private page addresses.',
  ],
  [
    'Release dry run',
    'The deployment package is validated and performance reports are produced before anything goes live.',
  ],
] as const;

function AboutUs() {
  return (
    <Container>
      <PageHero
        eyebrow='Principal-led practice'
        title='Work directly with your technical lead.'
        intro='Scaled Tech Consulting is a principal-led practice: you work with the person responsible for technical direction to understand the problem, weigh the options, and review progress as the work develops. When a project needs additional skills, we agree on roles, handoffs, and ownership.'
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 3,
        }}
      >
        {[
          {
            id: 'focus',
            label: 'Core focus',
            title: 'Focus',
            icon: AccountTreeOutlinedIcon,
            accent: tokens.secondary,
            text: 'We focus on web applications, content platforms, integrations, and the administrative workflows that connect them. The work begins with a clear understanding of who uses the system, what they need to do, and where the current process falls short.',
          },
          {
            id: 'background',
            label: 'Practice history',
            title: 'Background',
            icon: HistoryEduOutlinedIcon,
            accent: tokens.navy,
            text: (
              <>
                Scaled Tech draws on experience from Expert Web Coder, a predecessor software
                practice established in 2004. That work includes shared CMS platforms for distinct
                sites, multilingual publishing, data-fed portals, and administrative tools for
                structured content. We bring those lessons to each engagement while scoping the work
                to its own goals and constraints. Our principal has also worked with Facebook and
                with Microsoft, and helped build RBC&apos;s mobile banking platform in three
                versions: mobile web, Windows Phone, and Android. With a Microsoft team, the
                principal built RBC Mobile for Windows Phone 8 from start to finish in three months,
                and the app won an award. RBC then asked the principal to stay on as VP of Android
                development. At the app&apos;s 2014 launch, RBC was the{' '}
                <MuiLink
                  href='https://mobilesyrup.com/2014/02/06/royal-bank-of-canada-brings-it-rbc-mobile-app-to-windows-phone-8-first-major-canadian-bank-to-do-so/'
                  sx={{ color: tokens.accentText }}
                >
                  first major Canadian bank on Windows Phone
                </MuiLink>
                .
              </>
            ),
          },
        ].map(({ id, label, title, icon: Icon, accent, text }) => (
          <Box
            key={id}
            component='section'
            aria-labelledby={`about-${id}`}
            sx={{ ...cardSx, p: { xs: 3, md: 4 }, borderTop: `3px solid ${accent}` }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant='labelCaps'
                component='p'
                sx={{
                  color: tokens.accentText,
                  bgcolor: tokens.surfaceContainer,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {label}
              </Typography>
              <Icon
                aria-hidden='true'
                sx={{ color: tokens.accentText }}
              />
            </Box>
            <Typography
              id={`about-${id}`}
              component='h2'
              variant='h3'
              sx={{ mt: 2.5 }}
            >
              {title}
            </Typography>
            <Typography
              variant='body2'
              sx={{ mt: 1.5 }}
            >
              {text}
            </Typography>
          </Box>
        ))}
      </Box>

      <Section
        id='how-we-work'
        label='Discovery to delivery'
        title='How we work'
        card
        lead={
          <>
            <p>
              We map the workflow before choosing the tools. A useful solution may need a clear
              interface, an integration with an existing system, a better way to manage content, or
              all three. We explain the tradeoffs and build in stages that can be reviewed as the
              work progresses.
            </p>
            <p>The goal is software that people can use, understand, and maintain after launch.</p>
          </>
        }
      >
        <Box
          component='ol'
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 4,
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            bgcolor: tokens.surfaceLow,
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
          }}
        >
          {stages.map(([title, text], index) => (
            <li key={title}>
              <Typography
                variant='labelCaps'
                component='p'
                sx={{ color: tokens.accentText }}
              >
                Stage {String(index + 1).padStart(2, '0')}
              </Typography>
              <Typography
                component='h3'
                variant='h5'
                sx={{ mt: 0.5 }}
              >
                {title}
              </Typography>
              <Typography
                variant='bodySm'
                component='p'
                sx={{ mt: 0.5, color: tokens.onSurfaceVariant }}
              >
                {text}
              </Typography>
            </li>
          ))}
        </Box>
      </Section>

      <Section
        id='delivery-practices'
        label='How we maintain this website'
        title='Delivery practices'
        lead='These examples describe how we maintain this website. Project-specific requirements and support terms are agreed for each engagement.'
      >
        <NumberedCards
          items={[
            {
              title: 'Accessibility checks',
              body: (
                <>
                  We plan for accessibility in design and delivery, with project-specific goals and
                  testing agreed for each engagement.{' '}
                  <MuiLink
                    href='#accessibility-approach'
                    sx={{ color: tokens.accentText }}
                  >
                    Explore our accessibility approach
                  </MuiLink>
                  .
                </>
              ),
            },
            {
              title: 'Reviewed releases',
              body: (
                <>
                  Website changes are tested and reviewed before release. We verify the running site
                  after production deployment and keep deployment steps documented for handoff.{' '}
                  <MuiLink
                    href='#security-and-data'
                    sx={{ color: tokens.accentText }}
                  >
                    See our release checks
                  </MuiLink>
                  .
                </>
              ),
            },
            {
              title: 'Maintenance and support',
              body: 'For new work, we agree on who owns content updates, issue triage, operating notes, and ongoing support. Response times and service terms are set in the engagement, rather than assumed from this website.',
            },
            {
              title: 'Procurement contact',
              body: (
                <>
                  For a requested evaluation or procurement conversation, email{' '}
                  <MuiLink
                    href='mailto:info@scaledtech.consulting'
                    sx={{ color: tokens.accentText }}
                  >
                    info@scaledtech.consulting
                  </MuiLink>
                  . We can discuss the appropriate channel for project details and references.
                </>
              ),
            },
          ]}
        />
      </Section>

      <Section
        id='security-and-data'
        label='Security'
        title='How we protect your data'
        tone='low'
        lead={
          <>
            <p>
              This website is a working example of how we build. The contact form, the booking
              calendar, the accessibility options, and error monitoring are our own code on our own
              accounts, not third-party widgets that load someone else&apos;s scripts and see what
              visitors type or change.
            </p>
            <p>
              Where an outside service is needed, we keep the list short and name it: Cloudflare
              hosts and protects the site, screens forms for bots, and provides cookie-free page
              statistics, and bookings use our Google Calendar. The{' '}
              <MuiLink
                component={RouterLink}
                to='/privacy'
                sx={{ color: tokens.accentText }}
              >
                privacy notice
              </MuiLink>{' '}
              explains what each one receives.
            </p>
            <p>
              Security is part of everyday engineering, not a final step. We hold client work to the
              same practices, adjusted to each project&apos;s requirements.
            </p>
            <p>
              On client projects, we work with your team to find the privacy concerns your system
              needs to handle. With over five years in the banking industry, we treat privacy as a
              first concern, and we show you practical ways to reduce the risk of hacking attempts
              and data leakage.
            </p>
          </>
        }
      >
        <NumberedCards
          items={[
            {
              title: 'Less to leak',
              body: "Fonts and images come from our own domain, and the only outside scripts are Cloudflare's bot check and page statistics. There are no ad trackers, chat widgets, accessibility overlays, or social embeds, and error reports leave out form entries and private page addresses before they reach our own server.",
            },
            {
              title: 'Hardened responses',
              body: 'Pages are served over HTTPS only, with headers that stop other sites framing them, switch off browser features such as camera and location, and limit what leaves in the referrer. A content security policy lists the sources allowed to run code and reports anything else.',
            },
            {
              title: 'Checked input',
              body: 'The contact and booking services check every field on the server, cap message sizes, and require a bot-protection token before anything is sent or booked. The booking service also limits repeated attempts.',
            },
            {
              title: 'Traceable changes',
              body: 'Every change is a signed commit on its own branch, reviewed in a pull request, and merged only after the automated checks pass on that exact version. Nothing is pushed straight to the live code.',
            },
          ]}
        />
        <Box sx={{ ...cardSx, mt: 3, p: { xs: 3, md: 4 } }}>
          <Typography
            component='h3'
            variant='h4'
          >
            Before anything is released
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 1, maxWidth: '46rem' }}
          >
            Each change to this website passes about 30 automated steps before it can merge. After
            each release, a script checks every public page and its security headers on the live
            site.
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 1.5, maxWidth: '46rem' }}
          >
            Every bug we find becomes a new automated test, so each later version is checked for it
            before release. Our own error monitoring shows us what failed, and where, often before a
            visitor would think to report it, so bug reports from users are rare.
          </Typography>
          <Box
            component='ol'
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 3,
              p: 0,
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {releaseChecks.map(([title, text], index) => (
              <Box
                component='li'
                key={title}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant='labelCaps'
                  component='p'
                  aria-hidden='true'
                  sx={{ color: tokens.accentText }}
                >
                  Step {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography
                  component='h4'
                  variant='h5'
                  sx={{ mt: 0.5 }}
                >
                  {title}
                </Typography>
                <Typography
                  variant='bodySm'
                  component='p'
                  sx={{ mt: 0.5, color: tokens.onSurfaceVariant }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Section>

      <Section
        id='accessibility-approach'
        label='Standards'
        title='Our accessibility approach'
        card
        lead={
          <>
            <p>
              We plan for accessibility as part of design and delivery: clear structure, meaningful
              labels, keyboard access, readable contrast, and layouts that adapt to different
              screens and text sizes.
            </p>
            <p>
              For this website, automated checks scan public pages and exercise keyboard focus and
              responsive layouts. VoiceOver checks have also been performed on selected
              interactions. These checks help find issues but do not establish WCAG conformance or
              ADA compliance.
            </p>
            <p>
              For a client project, we agree on applicable accessibility targets, such as WCAG 2.2
              AA, and a testing plan for the actual content and workflows. Public-sector
              requirements can also cover documents and content supplied by others; the organization
              should determine its obligations with appropriate specialists.
            </p>
            <p>
              We discuss how authors will maintain accessible headings, links, image descriptions,
              and documents. When issues are found, the engagement can define how they are recorded,
              prioritized, corrected, retested, and maintained after launch.
            </p>
            <p>
              To discuss an accessibility plan for your project, email{' '}
              <MuiLink
                href='mailto:info@scaledtech.consulting'
                sx={{ color: tokens.accentText, overflowWrap: 'anywhere' }}
              >
                info@scaledtech.consulting
              </MuiLink>
              .
            </p>
          </>
        }
      />

      <CallBand />
    </Container>
  );
}

export default AboutUs;
